"use client";
import React, { useEffect, useLayoutEffect, forwardRef, useState, useRef } from "react";
import { Menu } from "antd";
import { usePathname, useRouter, useParams } from "next/navigation";
import { useIntl } from "react-intl";
import { routeTo } from "../CustomHooks/routeChange";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { _eventBus } from "../../core/events/EventBus";
import { flagSearchFlowNavigation } from "../../Lib/Utils/searchFlowNavigation";
import { evtTree } from "../../Orchestrator/ConstantTree";
import { useLocale } from "../Context/LocaleContext";

type MenuItemType = string;

interface BottomBarProps {
  menuItems: MenuItemType[];
  isSticky?: boolean;
}

const BottomBar = forwardRef<HTMLDivElement, BottomBarProps>(({ menuItems, isSticky = false }, ref) => {
  const router = useRouter();
  const { formatMessage, messages } = useIntl();
  const [isAuthenticated] = useRecState<boolean>("isAuthenticatedStateAtom");
  const { locale } = useLocale();

  const pathname = usePathname();
  const { network_id } = useParams();

  const isStationDetailsPage =
    network_id && pathname === `/networks-maps-stations/${network_id}`;

  // const [selectedMenu, setSelectedMenu] = useState("");
  const [selectedMenu] = useRecState<string>("selectedMenuAtom");

  const activeKey = isStationDetailsPage
    ? "networks-maps-stations"
    : selectedMenu;

  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 600;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };
    checkMobile();
    let raf: number;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(checkMobile);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Close the mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Close the mobile menu when clicking outside it or when the user scrolls
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleScroll = () => setIsMobileMenuOpen(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobileMenuOpen]);

  // const handleMenuClick = (key: string) => {
  //   setSelectedMenu(key);
  //   switch (key) {
  //     case "search":
  //       if (isAuthenticated) {
  //         routeTo("/search");
  //       } else {
  //         routeTo("/");
  //       }
  //       break;
  //     case "bookings":
  //       routeTo("/bookings");
  //       break;
  //     default:
  //       router.push("/");
  //       break;
  //   }
  // };

  useEffect(() => {
    setTimeout(() => {
      if (pathname === "/") {
        //setSelectedMenu("search");
        _eventBus.emit({
          eventName: evtTree.TopBar.Menu.Select,
          data: { path: "search" },
        });
      } else if (pathname.startsWith("/my-account")) {
        _eventBus.emit({
          eventName: evtTree.TopBar.Menu.Select,
          data: { path: "" },
        });
      }
      // else if (pathname.startsWith("/bookings")) {
      //   setSelectedMenu("bookings");
      // }
      else {
        //setSelectedMenu(pathname ? pathname.slice(1) : "");
        _eventBus.emit({
          eventName: evtTree.TopBar.Menu.Select,
          data: { path: pathname ? pathname.slice(1) : "" },
        });
      }
    }, 100);
  }, [pathname]);

  // Convert menuItems to the format required by the items prop
  const menuItemsFormatted = menuItems.map((item: string, index: number) => ({
    key: item, // Use item directly as key
    label: <span className="menu-name">{formatMessage({ id: item })}</span>, // Format message using react-intl
    // onClick: () => handleMenuClick(item),
    onClick: () => {
      _eventBus.emit({
        eventName: evtTree.TopBar.Menu.Select,
        data: { path: item, isAuthenticated: isAuthenticated, isRoute: true },
      });

      if (item === "search") {
        /**
         * This is required to start flow
         * If user is redirecting search page
         */
        // The route change to the search page is async, so the HomePageFlow restart
        // triggered by FLOW_CHANGE can run while window.location.pathname is still
        // "/train-offer". Flag the intent so MainBookingInit mounts the SearchFlow
        // (and its Search-button subscription) instead of the offer flows.
        flagSearchFlowNavigation();
        _eventBus.emit({
          eventName: "FLOW_CHANGE",
          data: "HomePageFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking details page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "BookingDetailFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "AfterSalesBookingFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "ChangeDateFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "FmlmViewFlow",
        });

        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "GroupBookingFlow",
        });
      }
      if (item === "bookings") {
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "HomePageFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting booking details page to search page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "BookingDetailFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "ChangeDateFlow",
        });
        /**
         * This is required to close because there is one possibility that
         * user is redirecting search page from booking page
         */
        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "FmlmViewFlow",
        });

        _eventBus.emit({
          eventName: "FLOW_CLOSE",
          data: "GroupBookingFlow",
        });
      }

      /**
       * We need to close the RequestCorporateAccountFlow
       * Once user click on any menu from the top bar.
       */
      _eventBus.emit({
        eventName: "FLOW_CLOSE",
        data: "RequestCorporateAccountFlow",
      });
    },
  }));

  const isRtl = locale === "ar";

  return (
    <div ref={ref} className={`navigation ${isSticky ? "navigation-sticky" : ""}`}>
      {isMobile ? (
        <div
          ref={mobileMenuRef}
          className="mobile-nav-bar"
          dir={isRtl ? "rtl" : "ltr"}
          style={isStationDetailsPage ? { borderBottom: "none" } : undefined}
        >
          <span className="mobile-nav-active-label">
            {/* selectedMenuAtom falls back to the raw pathname for unmapped
                routes (e.g. service-request-claim/<id>) — only show keys that
                have a real translation so the slug never leaks into the UI */}
            {activeKey && messages?.[activeKey] ? formatMessage({ id: activeKey }) : ""}
          </span>
          <button
            className={`hamburger-btn${isMobileMenuOpen ? " is-open" : ""}`}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-haspopup="true"
            aria-controls="mobile-menu-dropdown"
          >
            {isMobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <line x1="3" y1="3" x2="17" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="17" y1="3" x2="3" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <line x1="2" y1="5" x2="18" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="2" y1="10" x2="18" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="2" y1="15" x2="18" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>

          {isMobileMenuOpen && (
            <ul
              id="mobile-menu-dropdown"
              className="mobile-menu-dropdown"
              role="menu"
            >
              {menuItemsFormatted.map((item) => (
                <li
                  key={item.key}
                  role="menuitem"
                  tabIndex={0}
                  className={`mobile-menu-item${activeKey === item.key ? " active" : ""}`}
                  onClick={() => {
                    item.onClick?.();
                    setIsMobileMenuOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.onClick?.();
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <nav>
          <Menu
            key={`desktop-${locale}`}
            theme="light"
            mode="horizontal"
            items={menuItemsFormatted}
            selectedKeys={[activeKey]}
            style={{
              borderBottom: isStationDetailsPage ? "none" : undefined,
            }}
          />
        </nav>
      )}
    </div>
  );
});

BottomBar.displayName = "BottomBar";

export default BottomBar;
